import React, { useCallback, useEffect, useState } from 'react';

// Globals
import gql from 'graphql-tag';
import { batch, useDispatch, useSelector } from 'react-redux';

// UI
import { FeedCardView } from '@GiideLearning/giide-feedcards-v3';
import {
  Button,
  Col,
  Flex,
  message,
  Modal,
  Row,
  Skeleton,
  Tooltip,
} from 'antd';
import iconUnlinked from '../../studio/assets/icon_unlinked.png';
import opacityTransparentBackground from '../../studio/assets/feedcardIcons/opacityTransparentBackground.png';
import styles from '../Components.module.css';

// Locals
import {
  setEditingCardProps,
  setEditingChapterId,
  setImageChanged,
  setImageEditorImage,
  setRelinkingFeedcard,
  setShowButtons,
} from '../../actions';
import { CardType, DeleteFeedCardOutput } from '../../graphql/api';
import useRecording from '../../hooks/useQuery/useRecording';

// Types
import type { FeedCard, IState } from '../../graphql/types';
import { getSignedUrl } from '../utils';
import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { API } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';

const deleteFeedCard = gql`
  mutation DeleteFeedCardResources($input: DeleteFeedCardInput!) {
    deleteFeedCardResources(input: $input) {
      success
      error
    }
  }
`;

const cardsWithImages = new Set([
  CardType.graphic,
  CardType.profile,
  CardType.resource,
]);

type SpinnerProps = {
  sizePx?: number;
};

export const Spinner = ({ sizePx }: SpinnerProps) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        height: '100%',
        width: '100%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoadingOutlined
        style={sizePx ? { fontSize: `${sizePx}px` } : {}}
        className={styles.loadingIconGray}
        spin
      />
    </div>
  );
};

const Card: React.FC<
  Partial<FeedCard> & {
    stepIndex: number;
    graphicUrl?: string;
    setModal: () => void;
    stepOrder: number;
    openedStepIndex: number;
    allowRelinking: boolean;
  }
> = React.memo(
  ({
    id,
    recording,
    type,
    graphicUrl,
    setModal,
    stepOrder,
    openedStepIndex,
    allowRelinking,
    ...other
  }) => {
    const dispatch = useDispatch();

    const { fetchRecording } = useRecording();

    const highlightedCardId = useSelector<IState, string | null | undefined>(
      state => state.highlightedCardId
    );

    const showButtons = useSelector<IState, string | undefined>(
      state => state.showButtons
    );

    const [modal, contextHolder] = Modal.useModal();

    const relinkingFeedcard = useSelector<IState, string | undefined | null>(
      state => state.relinkingFeedcard
    );

    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

    const handleEdit = useCallback(async () => {
      batch(async () => {
        dispatch(setEditingChapterId(undefined));
        dispatch(setImageChanged(false));
        dispatch(
          setEditingCardProps({
            id,
            type,
            recording,
            imageBlob: imageUrl,
            ...other,
          })
        );

        if (type && cardsWithImages.has(type)) {
          dispatch(
            setImageEditorImage({
              objectUrl: imageUrl,
            })
          );
        }
      });
    }, [dispatch, id, other, recording, type, imageUrl]);

    const handleHover = useCallback(() => {
      if (
        showButtons !== id &&
        (!other.isInLibrary || other.nonLibraryGiideID)
      ) {
        dispatch(setShowButtons(id));
      }
    }, [showButtons, dispatch, id, other]);

    const handleNotHover = useCallback(() => {
      dispatch(setShowButtons());
    }, [dispatch]);

    const handleDelete = useCallback(async () => {
      if (recording && id) {
        batch(() => {
          dispatch(setEditingChapterId(undefined));
          dispatch(setShowButtons());
        });

        const deleteResult = (await API.graphql({
          query: deleteFeedCard,
          variables: { input: { id: id } },
        })) as GraphQLResult<{ deleteFeedCardResources: DeleteFeedCardOutput }>;

        if (!deleteResult.data?.deleteFeedCardResources.success) {
          let errorMsg =
            'An unknown error occurred while deleting this feed card.';

          if (deleteResult.data?.deleteFeedCardResources.error) {
            errorMsg = deleteResult.data?.deleteFeedCardResources.error;
          }

          message.error(errorMsg);
        } else {
          await fetchRecording(recording.id, true);
        }
      }
    }, [fetchRecording, dispatch, id, recording]);

    const onClickRelink = useCallback(() => {
      if (allowRelinking) {
        dispatch(setRelinkingFeedcard(relinkingFeedcard === id ? null : id!));
      }
    }, [id, dispatch, relinkingFeedcard, allowRelinking]);

    const currentImageUploads = useSelector<IState, Set<string>>(
      state => state.imageSubscriptions
    );

    const onDeleteFeedcard = React.useCallback(() => {
      modal.confirm({
        centered: true,
        closable: true,
        cancelText: 'Cancel',
        type: 'warn',
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          try {
            await handleDelete();
          } catch (err) {
            message.error('An error occurred deleting feed card.');
          }
          return true;
        },
        content: 'Are you sure that you want to delete this feed card?',
        title: 'Delete Feed Card',
      });
    }, [handleDelete, modal]);

    // TODO: Make overlay the card until the image is loaded
    useEffect(() => {
      // Fetches the graphic URL for this feedcard (if it has a custom image)
      // If the image is currently uploading, don't fetch the URL yet
      const fetchImageUrl = async () => {
        if (id && currentImageUploads.has(id)) {
          setImageLoaded(false);
          return;
        }

        if (type && cardsWithImages.has(type)) {
          const feedCardImageUrl =
            other.imageDetails?.url ||
            other.customImage ||
            other.imageUrl ||
            other.url;

          if (
            (feedCardImageUrl?.startsWith('blob:') ||
              feedCardImageUrl?.startsWith('https')) &&
            !other.customImage
          ) {
            setImageUrl(feedCardImageUrl);
            return;
          }

          if (other.url || other.customImage || other.imageDetails?.url) {
            let fileKey = `public/${decodeURIComponent(
              feedCardImageUrl as string
            )}`;

            if (other.imageService === 'cloudinary') {
              fileKey = `studio/${decodeURIComponent(
                feedCardImageUrl as string
              )}`;
            }

            try {
              const signedUrl = await getSignedUrl(
                fileKey,
                undefined,
                undefined,
                undefined,
                other.imageService
              );

              if (signedUrl.url) {
                setImageUrl(signedUrl.url);
              }
            } catch (e) {
              console.log(e);
            }
          }
        }
      };

      fetchImageUrl();
    }, [
      type,
      currentImageUploads,
      id,
      other.customImage,
      other.imageDetails?.url,
      other.imageService,
      other.imageUrl,
      other.url,
    ]);

    const imageStillUploading = id && currentImageUploads.has(id);
    const loadingGraphicUrl = !imageUrl && type && cardsWithImages.has(type);

    const [imageLoaded, setImageLoaded] = useState(false);

    const [imageTimeoutExceeded, setImageTimeoutExceeded] = useState(false);

    const [imageFailedToLoad, setImageFailedToLoad] = useState(false);

    useEffect(() => {
      const timeout = setTimeout(() => setImageTimeoutExceeded(true), 6000);

      return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
      const timeout = setTimeout(() => setImageFailedToLoad(true), 20000);

      return () => clearTimeout(timeout);
    }, []);

    let showSpinner =
      (type === CardType.graphic && !imageLoaded) ||
      (type === CardType.profile && !imageLoaded) ||
      (type === CardType.resource && other.customImage && !imageLoaded) ||
      imageStillUploading ||
      loadingGraphicUrl;

    return (
      <div style={{ position: 'relative' }}>
        {contextHolder}
        {!imageFailedToLoad && showSpinner && (
          <Skeleton.Image
            active={!imageTimeoutExceeded}
            style={{
              position: 'absolute',
              width: 'calc(100% - 8px - (12.375pt * 2))',
              top: 8,
              height: 'calc(100% - 16px)',
              boxSizing: 'border-box',
              borderRadius: '8px',
              margin: 'auto',
              left: '21px',
            }}
          ></Skeleton.Image>
        )}

        <Row
          style={{
            opacity: !showSpinner || imageFailedToLoad ? 1 : 0,
            transition: 'opacity 0.1s',
          }}
          className={
            highlightedCardId === id
              ? 'highlighted-feedcard-wrapper'
              : other.unlinked
              ? 'unlinked-feedcard-wrapper'
              : 'feedcard-wrapper'
          }
          id={id}
        >
          <Tooltip
            placement="left"
            title={
              other.unlinked && stepOrder === openedStepIndex && !allowRelinking
                ? `Once you've re-uploaded audio for this chapter, you can relink feedcards.`
                : undefined
            }
          >
            <Col span={24}>
              <div
                onMouseOver={handleHover}
                onMouseLeave={handleNotHover}
                key={id}
                className={styles.feedcardControls}
              >
                <Row
                  justify="end"
                  className={`${styles.feedCardMenu} ${
                    showButtons === id ? '' : styles.feedCardMenuHidden
                  } ${
                    type === CardType.profile
                      ? styles.feedCardMenuOffsetProfile
                      : ''
                  } ${
                    type === CardType.resource
                      ? styles.feedCardMenuOffsetResource
                      : ''
                  }`}
                >
                  {showButtons === id && (
                    <img
                      className={`${
                        other.unlinked
                          ? styles.opacityTransparentBackgroundUnlinkedCard
                          : styles.opacityTransparentBackground
                      }`}
                      src={opacityTransparentBackground}
                      alt="50% opacity background"
                    />
                  )}
                  <Flex gap={'small'} className={styles.fcIcons}>
                    <Button
                      shape={'circle'}
                      onClick={handleEdit}
                      className={styles.editIconWrapper}
                    >
                      <EditOutlined />
                    </Button>
                    <Button
                      danger={true}
                      type={'primary'}
                      shape={'circle'}
                      onClick={onDeleteFeedcard}
                      className={styles.deleteIconWrapper}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Flex>
                </Row>
                {other.unlinked && (
                  <div className={styles.unlinkedIcon}>
                    <img src={iconUnlinked} alt="icon for unlinked feedcard" />
                  </div>
                )}
                {other.unlinked &&
                  stepOrder === openedStepIndex &&
                  allowRelinking &&
                  (!relinkingFeedcard || relinkingFeedcard === id) && (
                    <div className={styles.unlinkedFeedcardOverlay}>
                      <Button onClick={onClickRelink}>
                        {relinkingFeedcard && relinkingFeedcard === id
                          ? 'Click a word to relink. Or click here to cancel'
                          : 'Relink'}
                      </Button>
                    </div>
                  )}
                <div className={styles.disableClicking}>
                  <FeedCardView
                    // @ts-ignore
                    type={type}
                    {...other}
                    imageDetails={
                      type === CardType.graphic && imageUrl
                        ? {
                            ...(other.imageDetails as any),
                            url: imageUrl,
                          }
                        : undefined
                    }
                    url={
                      type === CardType.graphic
                        ? imageUrl
                        : CardType.resource
                        ? other.url
                        : undefined
                    }
                    imageUrl={
                      (type === CardType.resource && other.imageUrl) ||
                      (type === CardType.resource && other.customImage)
                        ? imageUrl
                        : undefined
                    }
                    headshotPath={
                      type === CardType.profile ? imageUrl : undefined
                    }
                    openModal={type === CardType.quiz ? setModal : undefined}
                    onLoaded={() => setImageLoaded(true)}
                    listItems={
                      type === CardType.list && other.listItems
                        ? (other.listItems.filter(item => item) as string[])
                        : undefined
                    }
                  />
                </div>
              </div>
            </Col>
          </Tooltip>
        </Row>
      </div>
    );
  }
);

export default Card;
